import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  Renderer2,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DowntimeNotificationComponent } from '@frontend/common/downtime-notification';
import { IdleService } from '@frontend/common/idle';
import { PhAuthService } from '@frontend/common/ph-auth';
import { PhConfigLoaderService } from '@frontend/common/ph-config-loader';
import {
  CurrentDepartmentService,
  CurrentUserService,
  Document360Service,
  ProductId,
  ProductTranslation,
  SocketService,
  UserNotificationService,
} from '@frontend/common/util';
import { customersSelectors } from '@frontend/entity';
import { PatientSearchComponent } from '@frontend/workflow-rt/feature/patient-search/src';
import { DepartmentSelectorComponent } from './department-selector/department-selector.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { select, Store } from '@ngrx/store';
import { utc } from 'moment';
import { Observable } from 'rxjs';
import {
  defaultIfEmpty,
  distinctUntilChanged,
  filter,
  first,
  map,
  mergeMap,
  share,
  startWith,
} from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AppContentWrapperService } from './app-content-wrapper.service';

@UntilDestroy()
@Component({
  selector: 'wrt-content-wrapper',
  templateUrl: './app-content-wrapper.component.html',
  styleUrls: ['./app-content-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AppContentWrapperService],
  standalone: false
})
export class AppContentWrapperComponent implements OnInit {
  ProductTranslation = ProductTranslation;
  ProductId = ProductId;

  currentDate = utc().format('YYYY');
  hasNotifications$: Observable<any>;
  version = environment.version;

  accessReports$: Observable<boolean>;
  accessContactTracing$: Observable<boolean>;
  userSelectedDepartmentId$ = this.currentUserSrv.getPreference('department_id');
  constructor(
    public srv: AppContentWrapperService,
    private store: Store,
    private config: PhConfigLoaderService,
    private idleSrv: IdleService,
    private socketService: SocketService,
    private renderer: Renderer2,
    private document360Srv: Document360Service,
    private currentUserSrv: CurrentUserService,
    private userNotificationSrv: UserNotificationService,
    private bottomSheet: MatBottomSheet,
    private phAuthSrv: PhAuthService,
    private currentDepartmentSrv: CurrentDepartmentService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.hasNotifications$ = this.userNotificationSrv.notification$;

    this.accessReports$ = this.phAuthSrv.scopes$.pipe(
      map((scopes: any[]) => scopes.includes('ui:wrt:reports'))
    );

    this.accessContactTracing$ = this.phAuthSrv.scopes$.pipe(
      map((scopes: any[]) => scopes.includes('ui:wrt:contact-tracing'))
    );
  }

  ngOnInit() {
    const resolvedDepartment = this.route.snapshot.data?.currentDepartment;
    const departmentId = this.route.snapshot.params?.department_id;

    if (!resolvedDepartment && departmentId) {
      this.ensureDepartmentSelectorDialogOpen();
      this.navigateToDepartmentSelector();
      return;
    }

    this.route.params.pipe(
      map((params) => params.department_id),
      startWith(this.route.snapshot.params.department_id),
      filter((id) => !!id),
      distinctUntilChanged(),
      untilDestroyed(this)
    ).subscribe((deptId) => {
      this.currentDepartmentSrv.load(deptId);
    });
    const customer$ = this.store.pipe(
      select(customersSelectors.getCurrentCustomer),
      filter((customer) => !!customer),
      first(),
      share(),
      untilDestroyed(this)
    );
    customer$.subscribe((customer: any) => {
      const { auto_logout_seconds } = customer;
      this.idleSrv.timeout = auto_logout_seconds * 1000;
      this.idleSrv.setupListeners(this.renderer);
      this.idleSrv.idle.subscribe(() => this.phAuthSrv.logout());
    });
    this.socketService.fromEvent('logout').subscribe(() => this.phAuthSrv.logout());

    this.userNotificationSrv.notification$
      .pipe(
        mergeMap((data: any) =>
          this.currentUserSrv.getPreference(`ca_notification_${data.id}_read`).pipe(
            first(),
            filter((read) => !read),
            mergeMap(() => this.openDowntimeNotification(data))
          )
        )
      )
      .subscribe(
        (id) => id && this.currentUserSrv.save(`ca_notification_${id}_read`, true)
      );
  }

  openNotification() {
    this.userNotificationSrv.notification$
      .pipe(defaultIfEmpty(null))
      .subscribe((data) => this.openDowntimeNotification(data));
  }

  openPatientSearch() {
    this.dialog.open(PatientSearchComponent, { minWidth: '696px' });
  }

  private navigateToDepartmentSelector(): void {
    if (!this.router.url.includes('department-selector')) {
      this.router.navigateByUrl('/department-selector', { replaceUrl: true });
    }
  }

  private ensureDepartmentSelectorDialogOpen(): void {
    const existingDialog = this.dialog.openDialogs.find(
      (ref) => ref.componentInstance instanceof DepartmentSelectorComponent
    );

    if (!existingDialog) {
      this.dialog.open(DepartmentSelectorComponent, {
        width: '500px',
        panelClass: ['no-padding'],
        disableClose: true,
        closeOnNavigation: false,
        autoFocus: true,
      });
    }
  }

  private openDowntimeNotification(userNotification) {
    return this.bottomSheet
      .open(DowntimeNotificationComponent, {
        disableClose: true,
        hasBackdrop: false,
        data: { userNotification },
      })
      .afterDismissed();
  }
}
